import { MAX_TRACK_CELL_OCCUPANCY } from '@multirx/shared/game/trackMovement.js';
import type { Circuit } from '@multirx/shared/types/track.js';

/** Part du path visible dans une case grille (aligné TrackRenderer.PATH_CELL_RATIO). */
export const PATH_CELL_VISUAL_RATIO = 0.8;

/** Diamètre du pion à l'échelle 1, en fractions de case (`tokenDisplaySize` / cellSize). */
export const TOKEN_DISPLAY_FOOTPRINT = PATH_CELL_VISUAL_RATIO * 0.96;

/**
 * Largeur latérale d'un pion/voiture (pas la longueur). Deux files à taille 1
 * tiennent dans la bande de piste sans se recouvrir.
 */
export const TOKEN_LATERAL_FOOTPRINT = 0.22;

/** Jeu minimal entre les bords des deux emprises (fraction de case). */
export const PAIR_EDGE_GAP = 0.04;

export interface TokenCellLayout {
  offsetX: number;
  offsetY: number;
  scale: number;
}

/** Sens du path : de `pathIndex` vers la case suivante (modulo). */
export function getPathForwardDelta(
  circuit: Circuit,
  pathIndex: number
): { dx: number; dy: number } {
  const len = circuit.path.length;
  if (len === 0) return { dx: 1, dy: 0 };
  const i = ((pathIndex % len) + len) % len;
  const next = (i + 1) % len;
  const cur = circuit.path[i];
  const nxt = circuit.path[next];
  return { dx: nxt.x - cur.x, dy: nxt.y - cur.y };
}

/**
 * Perpendiculaire « prioritaire » (prochain à jouer sur la case).
 * Ex. ligne droite vers la droite (+X) → offset vers le haut (−Y écran).
 */
export function priorityPerpendicular(dx: number, dy: number): { px: number; py: number } {
  if (dx === 0 && dy === 0) return { px: 0, py: -1 };
  return { px: dy, py: -dx };
}

/**
 * Deux files latérales à **taille normale** (scale 1).
 * L'écart utilise la largeur (`footprint`), pas la longueur du modèle.
 * Au-delà de `MAX_TRACK_CELL_OCCUPANCY`, le surplus réutilise la 2e file.
 */
export function computeSideBySidePairLayout(
  forward: { dx: number; dy: number },
  stackIndex: number,
  cellSize: number,
  footprint: number,
  edgeGap: number = PAIR_EDGE_GAP
): TokenCellLayout {
  const gap = edgeGap * cellSize;
  const radius = (footprint * cellSize) / 2;
  const spread = radius + gap / 2;
  const { px, py } = priorityPerpendicular(forward.dx, forward.dy);
  const len = Math.hypot(px, py) || 1;
  const nx = px / len;
  const ny = py / len;
  const slotIndex = Math.min(Math.max(0, stackIndex), MAX_TRACK_CELL_OCCUPANCY - 1);
  const sign = slotIndex === 0 ? 1 : -1;
  return {
    offsetX: nx * spread * sign,
    offsetY: ny * spread * sign,
    scale: 1,
  };
}

/**
 * Position / échelle d'un pion sur une case.
 * @param stackIndex 0 = prioritaire (stamp le plus bas, joue en premier sur la case)
 * @param peerCount 1 = case entière ; 2 = files gauche/droite (max 2)
 */
export function computeTokenCellLayout(
  cellSize: number,
  forward: { dx: number; dy: number },
  stackIndex: number,
  peerCount: number
): TokenCellLayout {
  if (peerCount <= 1) {
    return { offsetX: 0, offsetY: 0, scale: 1 };
  }
  return computeSideBySidePairLayout(forward, stackIndex, cellSize, TOKEN_LATERAL_FOOTPRINT);
}

/** Diamètre cible du pion en unités monde (taille normale, seule ou à deux). */
export function tokenDisplaySize(cellSize: number, layoutScale: number): number {
  return cellSize * TOKEN_DISPLAY_FOOTPRINT * layoutScale;
}
